import React, { useState } from 'react';
import {
  Box, Container, Grid, Typography, Skeleton, Button, Collapse,
  IconButton, Table, TableHead, TableBody, TableRow, TableCell, Link,
} from '@mui/material';
import {
  YouTube, Podcasts, Favorite, ShoppingCart, Forum, MusicVideo,
  CameraAlt, People, AttachMoney, LaunchRounded, LocalShipping,
  ExpandMore, ExpandLess,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  youtubeAPI, megaphoneAPI, patreonAPI, shopifyAPI,
  redditAPI, tiktokAPI, metaAPI, masterCalendarAPI,
  episodeCorrelationAPI, youtubeRetentionAPI, youtubeTrafficMixAPI, fanLadderAPI,
  podcastListenerCurveAPI,
} from '../services/api';
import SectionTitle from './crt/SectionTitle';
import Panel from './crt/Panel';
import ReleaseCalendar, { CalendarEvent, LegendEntry } from './ReleaseCalendar';

const fmt = (n: number | null | undefined) =>
  n == null ? '—' : Number(n).toLocaleString();
const fmtMoney = (n: number | null | undefined) =>
  n == null ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtPct = (n: number | null | undefined) =>
  n == null ? '—' : `${Number(n).toFixed(1)}%`;

// ── Unfulfilled-orders banner ──────────────────────────────────────────────
// Sticky red panel at the top of the Master dashboard so new Shopify orders
// get noticed + shipped ASAP. Expands inline to show the order list.

interface UnfulfilledLineItem {
  title: string;
  variant_title: string | null;
  quantity: number;
  price: number;
  sku: string | null;
}

interface UnfulfilledOrder {
  id: string;
  order_number: number;
  name: string;
  email: string;
  created_at: string;
  age_days: number;
  total_price: number;
  currency: string;
  fulfillment_status: string;
  line_item_count: number | null;
  admin_url: string | null;
  line_items: UnfulfilledLineItem[];
}

interface UnfulfilledData {
  count: number;
  total_value: number;
  oldest_days: number;
  orders: UnfulfilledOrder[];
}

const UnfulfilledBanner: React.FC<{ data?: UnfulfilledData; onGo: () => void }> = ({ data, onGo }) => {
  const [open, setOpen] = useState(true); // default expanded — user asked to be noticed
  const [expandedId, setExpandedId] = useState<string | null>(null); // per-row accordion
  if (!data || data.count === 0) return null;

  const urgent = data.oldest_days >= 7;
  const accent = urgent ? '#ff3344' : '#ffb000';

  return (
    <Box
      sx={{
        mb: 3,
        border: `2px solid ${accent}`,
        background: 'linear-gradient(90deg, rgba(255,51,68,0.08), rgba(255,51,68,0.02))',
        position: 'relative',
      }}
    >
      {/* Ribbon label */}
      <Typography
        variant="caption"
        sx={{
          position: 'absolute', top: -10, left: 16, px: 1,
          background: '#0a0a0a', color: accent,
          fontFamily: "'Press Start 2P', monospace", fontSize: 9, letterSpacing: '0.1em',
        }}
      >
        // ACTION REQUIRED //
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3, pt: 3.5 }}>
        <LocalShipping sx={{ color: accent, fontSize: 44, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h5"
            sx={{ color: accent, fontFamily: "'Press Start 2P', monospace", fontSize: 14, letterSpacing: '0.05em' }}
          >
            {data.count} ORDER{data.count === 1 ? '' : 'S'} AWAITING SHIPMENT
          </Typography>
          <Typography variant="body1" sx={{ color: '#5aaa5a', mt: 0.5 }}>
            &gt; Oldest unfulfilled: <strong>{data.oldest_days} day{data.oldest_days === 1 ? '' : 's'}</strong> old
            {' · '}Total pending value:{' '}
            <strong>${data.total_value.toLocaleString(undefined, { maximumFractionDigits: 2 })}</strong>
          </Typography>
        </Box>
        <Button
          onClick={onGo}
          variant="outlined"
          sx={{
            color: accent, borderColor: accent,
            fontFamily: "'Press Start 2P', monospace", fontSize: 9, letterSpacing: '0.08em',
            '&:hover': { borderColor: accent, background: `${accent}14` },
          }}
        >
          [ OPEN SHOPIFY ]
        </Button>
        <IconButton onClick={() => setOpen(!open)} sx={{ color: accent }}>
          {open ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      <Collapse in={open}>
        <Box sx={{ px: 3, pb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: '#5aaa5a', fontSize: 11, letterSpacing: '0.08em' }}>ORDER #</TableCell>
                <TableCell sx={{ color: '#5aaa5a', fontSize: 11, letterSpacing: '0.08em' }}>CUSTOMER</TableCell>
                <TableCell sx={{ color: '#5aaa5a', fontSize: 11, letterSpacing: '0.08em' }}>STATUS</TableCell>
                <TableCell align="right" sx={{ color: '#5aaa5a', fontSize: 11, letterSpacing: '0.08em' }}>AGE</TableCell>
                <TableCell align="right" sx={{ color: '#5aaa5a', fontSize: 11, letterSpacing: '0.08em' }}>VALUE</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.orders.map((o) => {
                const ageColor = o.age_days >= 14 ? '#ff3344' : o.age_days >= 7 ? '#ffb000' : '#00ff41';
                const isOpen = expandedId === o.id;
                return (
                  <React.Fragment key={o.id}>
                    <TableRow
                      hover
                      onClick={() => setExpandedId(isOpen ? null : o.id)}
                      sx={{
                        cursor: 'pointer',
                        '& td': { borderBottom: isOpen ? 0 : undefined },
                      }}
                    >
                      <TableCell sx={{ fontFamily: "'Share Tech Mono', monospace", color: isOpen ? accent : undefined }}>
                        {isOpen ? '▼' : '▶'} {o.name || `#${o.order_number}`}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "'VT323', monospace" }} onClick={(e) => e.stopPropagation()}>
                        <Link
                          href={`mailto:${o.email}`}
                          sx={{ color: '#00ffff', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {o.email}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ color: '#5aaa5a', textTransform: 'uppercase' }}>
                          {o.fulfillment_status}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ color: ageColor, fontWeight: 600 }}>
                        {o.age_days}d
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#00ff41' }}>
                        ${o.total_price.toFixed(2)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                        <Collapse in={isOpen} unmountOnExit>
                          <Box
                            sx={{
                              p: 2,
                              mx: 1,
                              mb: 1,
                              border: `1px dashed ${accent}`,
                              background: 'rgba(0,0,0,0.4)',
                            }}
                          >
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1.5 }}>
                              <Typography variant="caption" sx={{ color: '#5aaa5a' }}>
                                &gt; ORDER_ID: <span style={{ color: '#00ff41' }}>{o.id}</span>
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#5aaa5a' }}>
                                &gt; PLACED: <span style={{ color: '#00ff41' }}>{new Date(o.created_at).toLocaleString()}</span>
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#5aaa5a' }}>
                                &gt; ITEMS: <span style={{ color: '#00ff41' }}>{o.line_item_count || o.line_items.length}</span>
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#5aaa5a' }}>
                                &gt; CURRENCY: <span style={{ color: '#00ff41' }}>{o.currency}</span>
                              </Typography>
                            </Box>

                            {o.line_items.length > 0 && (
                              <Box sx={{ mb: 1.5 }}>
                                <Typography variant="caption" sx={{ color: '#5aaa5a', display: 'block', mb: 0.5 }}>
                                  &gt; LINE ITEMS
                                </Typography>
                                {o.line_items.map((li, i) => (
                                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.25 }}>
                                    <Typography variant="body2" sx={{ color: '#00ff41' }}>
                                      {li.quantity}× {li.title}
                                      {li.variant_title && (
                                        <span style={{ color: '#5aaa5a' }}> — {li.variant_title}</span>
                                      )}
                                      {li.sku && (
                                        <span style={{ color: '#5aaa5a', fontSize: 12 }}> · SKU {li.sku}</span>
                                      )}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#00ff41' }}>
                                      ${(li.price * li.quantity).toFixed(2)}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}

                            <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
                              {o.admin_url && (
                                <Button
                                  size="small"
                                  component="a"
                                  href={o.admin_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  endIcon={<LaunchRounded fontSize="small" />}
                                  variant="outlined"
                                  sx={{
                                    color: accent, borderColor: accent,
                                    fontFamily: "'Press Start 2P', monospace",
                                    fontSize: 9, letterSpacing: '0.08em',
                                    '&:hover': { borderColor: accent, background: `${accent}14` },
                                  }}
                                >
                                  [ OPEN IN SHOPIFY ]
                                </Button>
                              )}
                              <Button
                                size="small"
                                component="a"
                                href={`mailto:${o.email}?subject=Your%20order%20${encodeURIComponent(o.name || `#${o.order_number}`)}`}
                                onClick={(e) => e.stopPropagation()}
                                sx={{ color: '#00ffff', fontSize: 10, letterSpacing: '0.08em' }}
                              >
                                [ EMAIL CUSTOMER ]
                              </Button>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Collapse>
    </Box>
  );
};

const PLATFORM_COLORS: Record<string, string> = {
  youtube_vod:      '#ff3344',
  youtube_short:    '#ff6600',
  podcast_episode:  '#00ffff',
  patreon_post:     '#ff6600',
  tiktok_video:     '#00ff41',
  shopify_launch:   '#ffb000',
  reddit_post:      '#ff4500',
};

const CALENDAR_LEGEND: LegendEntry[] = [
  { type: 'youtube_vod',     label: 'YouTube video',  color: PLATFORM_COLORS.youtube_vod },
  { type: 'youtube_short',   label: 'YouTube short',  color: PLATFORM_COLORS.youtube_short },
  { type: 'podcast_episode', label: 'Podcast',        color: PLATFORM_COLORS.podcast_episode },
  { type: 'patreon_post',    label: 'Patreon post',   color: PLATFORM_COLORS.patreon_post },
  { type: 'tiktok_video',    label: 'TikTok clip',    color: PLATFORM_COLORS.tiktok_video },
  { type: 'shopify_launch',  label: 'Shopify launch', color: PLATFORM_COLORS.shopify_launch },
  { type: 'reddit_post',     label: 'Reddit post',    color: PLATFORM_COLORS.reddit_post },
];

// ── Cross-platform episode correlation ────────────────────────────────────
// For each podcast episode, surface what landed on YouTube / TikTok /
// Patreon during that release window. The data is ALWAYS in the DB but
// nothing aggregates it together — this row is the single most useful
// signal for "did this episode get its full promo cycle".

interface EpisodeCorrelationRow {
  episode_id: string;
  title: string;
  season: number | null;
  episode: number | null;
  publish_date: string | null;
  downloads: number;
  youtube_vods: number;
  youtube_vod_views: number;
  youtube_shorts: number;
  youtube_short_views: number;
  tiktok_clips: number;
  tiktok_views: number;
  patreon_posts: number;
  patreon_paid_posts: number;
}

interface EpisodeCorrelationData {
  episodes: EpisodeCorrelationRow[];
  count: number;
}

const EpisodeCorrelationPanel: React.FC<{ data?: EpisodeCorrelationData }> = ({ data }) => {
  const episodes = data?.episodes || [];
  return (
    <Panel label="// CROSS-PLATFORM EPISODE CORRELATION //">
      <Typography
        variant="caption"
        sx={{ color: '#5aaa5a', display: 'block', mb: 1, fontFamily: "'VT323', monospace" }}
      >
        &gt; for each episode: downloads + YouTube VODs/shorts, TikTok clips, Patreon posts
        published before the next release_
      </Typography>
      {episodes.length === 0 ? (
        <Skeleton variant="rectangular" height={280} />
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{
            '& th': { color: '#00ff41', borderColor: '#1e3a1e', fontFamily: "'VT323', monospace", fontSize: 14 },
            '& td': { color: '#bfffbf', borderColor: '#0e1f0e', fontFamily: "'VT323', monospace", fontSize: 15 },
          }}>
            <TableHead>
              <TableRow>
                <TableCell>Episode</TableCell>
                <TableCell align="right">Downloads</TableCell>
                <TableCell align="right" sx={{ color: '#ff3344 !important' }}>YT VODs</TableCell>
                <TableCell align="right" sx={{ color: '#ff6600 !important' }}>YT shorts</TableCell>
                <TableCell align="right" sx={{ color: '#00ffff !important' }}>TikTok</TableCell>
                <TableCell align="right" sx={{ color: '#ff6600 !important' }}>Patreon</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {episodes.map((e) => {
                const epLabel = e.season && e.episode
                  ? `S${String(e.season).padStart(2, '0')}E${String(e.episode).padStart(2, '0')}`
                  : '—';
                return (
                  <TableRow key={e.episode_id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" sx={{ fontFamily: "'VT323', monospace", color: '#00ff41' }}>
                          {epLabel} — {(e.title || '').replace(/^S\d+E\d+\s*[-–:]\s*/, '') || '(untitled)'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#5aaa5a', fontFamily: "'VT323', monospace" }}>
                          {e.publish_date ? new Date(e.publish_date).toLocaleDateString() : '—'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">{fmt(e.downloads)}</TableCell>
                    <TableCell align="right">
                      {e.youtube_vods}
                      {e.youtube_vod_views > 0 && (
                        <Typography variant="caption" component="div" sx={{ color: '#5aaa5a' }}>
                          {fmt(e.youtube_vod_views)} views
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {e.youtube_shorts}
                      {e.youtube_short_views > 0 && (
                        <Typography variant="caption" component="div" sx={{ color: '#5aaa5a' }}>
                          {fmt(e.youtube_short_views)} views
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {e.tiktok_clips}
                      {e.tiktok_views > 0 && (
                        <Typography variant="caption" component="div" sx={{ color: '#5aaa5a' }}>
                          {fmt(e.tiktok_views)} views
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {e.patreon_posts}
                      {e.patreon_paid_posts > 0 && (
                        <Typography variant="caption" component="div" sx={{ color: '#5aaa5a' }}>
                          {e.patreon_paid_posts} paid
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Panel>
  );
};

// ── YouTube retention heatmap ─────────────────────────────────────────────
// Each row is one video; each column is 1% through the video. Cell color is
// audience_watch_ratio (1.0 = average for videos of similar length, >1 hot,
// <1 cold). Hot first column means strong hook; cold cliffs in the middle
// mean drop-off points. Surfaces the YT-API retention data that's been
// collected for ages but never visualized.

interface RetentionPoint { pct: number; watch: number; }
interface RetentionVideo {
  video_id: string;
  title: string;
  is_short: boolean;
  duration_seconds: number | null;
  published_at: string | null;
  curve: RetentionPoint[];
}

const retentionColor = (watch: number): string => {
  // Diverge around 1.0. Above 1 → green (good), below 1 → orange/red (bad).
  if (watch == null) return '#0a1f0a';
  // Clamp to [0, 2] so a single outlier doesn't wash out the gradient.
  const v = Math.max(0, Math.min(2, watch));
  if (v >= 1) {
    // 1.0 → muted green; 2.0 → bright neon
    const intensity = Math.round(40 + (v - 1) * 200); // 40..240
    return `rgb(0, ${Math.min(255, intensity)}, 65)`;
  }
  // 1.0 → muted; 0 → red
  const fade = Math.round(v * 255); // 0..255
  return `rgb(${255 - fade}, ${Math.round(fade * 0.4)}, 0)`;
};

const RetentionHeatmap: React.FC<{ data?: { videos: RetentionVideo[] } }> = ({ data }) => {
  const videos = data?.videos || [];
  return (
    <Panel label="// YOUTUBE RETENTION HEATMAP //">
      <Typography
        variant="caption"
        sx={{ color: '#5aaa5a', display: 'block', mb: 1, fontFamily: "'VT323', monospace" }}
      >
        &gt; each row = one video; each column = % through video; green = above-average retention, red = drop-off_
      </Typography>
      {videos.length === 0 ? (
        <Skeleton variant="rectangular" height={400} />
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '320px 1fr',
            gap: 0.5,
            minWidth: 800,
          }}>
            {videos.map((v) => (
              <React.Fragment key={v.video_id}>
                <Box sx={{
                  fontFamily: "'VT323', monospace",
                  color: v.is_short ? '#ff6600' : '#00ff41',
                  fontSize: 13,
                  pr: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  borderRight: '1px solid #1e3a1e',
                  display: 'flex',
                  alignItems: 'center',
                }}>
                  <Link
                    href={`https://youtu.be/${v.video_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: '#fff' } }}
                  >
                    {v.is_short ? '▸' : '◆'} {(v.title || '(untitled)').slice(0, 40)}{(v.title || '').length > 40 ? '…' : ''}
                  </Link>
                </Box>
                <Box sx={{ display: 'flex', height: 18, gap: 0 }}>
                  {(v.curve || []).map((p, i) => {
                    const pct = p?.pct == null ? 0 : Math.round(p.pct * 100);
                    const watch = p?.watch;
                    const watchLabel = watch == null ? 'no data' : `${Number(watch).toFixed(2)}× avg`;
                    return (
                      <Box
                        key={i}
                        title={`${pct}%: ${watchLabel}`}
                        sx={{
                          flex: 1,
                          background: retentionColor(watch as number),
                          borderRight: i % 10 === 9 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                        }}
                      />
                    );
                  })}
                </Box>
              </React.Fragment>
            ))}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, fontFamily: "'VT323', monospace", fontSize: 12 }}>
            <Typography variant="caption" sx={{ color: '#5aaa5a' }}>scale:</Typography>
            {[0, 0.5, 1, 1.25, 1.5, 1.75, 2].map((v) => (
              <Box key={v} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 16, height: 12, background: retentionColor(v) }} />
                <Typography variant="caption" sx={{ color: '#5aaa5a' }}>{v.toFixed(2)}×</Typography>
              </Box>
            ))}
            <Typography variant="caption" sx={{ color: '#5aaa5a', ml: 'auto' }}>
              ◆ VOD &nbsp; ▸ Short
            </Typography>
          </Box>
        </Box>
      )}
    </Panel>
  );
};

// ── YouTube traffic-source mix (weekly stacked area) ──────────────────────
// Stacked area of weekly views by traffic source. SHORTS, SUBSCRIBER, SEARCH,
// SUGGESTED, BROWSE — relative height per week. Catches algorithm shifts
// inside a week (vs the quarterly view in YouTube Studio).

const TRAFFIC_COLORS: Record<string, string> = {
  SHORTS:               '#ff6600',
  SUBSCRIBER:           '#00ff41',
  YT_OTHER_PAGE:        '#00ffff',
  YT_CHANNEL:           '#ffb000',
  PLAYLIST:             '#ff3344',
  YT_SEARCH:            '#9d4edd',
  NOTIFICATION:         '#ff80ff',
  RELATED_VIDEO:        '#5aaa5a',
  NO_LINK_OTHER:        '#666666',
  EXT_URL:              '#bfffbf',
  SHORTS_CONTENT_LINKS: '#ffea00',
  END_SCREEN:           '#888888',
};

const TrafficMixChart: React.FC<{
  data?: { weeks: any[]; sources: string[] };
}> = ({ data }) => {
  const weeks = data?.weeks || [];
  const sources = data?.sources || [];
  return (
    <Panel label="// YOUTUBE DISCOVERY MIX (WEEKLY) //">
      <Typography
        variant="caption"
        sx={{ color: '#5aaa5a', display: 'block', mb: 1, fontFamily: "'VT323', monospace" }}
      >
        &gt; how viewers find your videos, week by week — algorithm shifts show up as bands changing relative width_
      </Typography>
      {weeks.length === 0 ? (
        <Skeleton variant="rectangular" height={320} />
      ) : (
        <ResponsiveContainer width="100%" height={340}>
          <AreaChart data={weeks} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e3a1e" />
            <XAxis dataKey="week" stroke="#5aaa5a" tick={{ fontSize: 11 }} />
            <YAxis stroke="#5aaa5a" tick={{ fontSize: 11 }} />
            <Tooltip
              contentStyle={{
                background: '#0f0f0f',
                border: '1px solid #1e3a1e',
                color: '#00ff41',
                fontFamily: "'VT323', monospace",
              }}
              formatter={(v: number) => fmt(v)}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {sources.map((s) => (
              <Area
                key={s}
                type="monotone"
                dataKey={s}
                stackId="1"
                stroke={TRAFFIC_COLORS[s] || '#5aaa5a'}
                fill={TRAFFIC_COLORS[s] || '#5aaa5a'}
                fillOpacity={0.7}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
};

// ── Fan-ladder funnel ─────────────────────────────────────────────────────
// YT subs → Patreon free → Patreon paid → Shopify customers, with
// commitment-depth conversion %s. Reveals where engagement bleeds out
// AND how much of merch revenue comes from existing patrons (overlap).

interface FunnelStep {
  key: string;
  label: string;
  sublabel?: string;
  count: number;
  color: string;
  pct_of_top: number;
  pct_of_prev: number;
  note?: string;
  lever_to_next?: string | null;
}

interface FanLadderData {
  steps: FunnelStep[];
  merch: {
    shopify_customers: number;
    patron_and_customer: number;
    patron_and_customer_pct: number;
    customer_and_patron_pct: number;
    shopify_ltv_dollars: number;
  };
  revenue: {
    patreon_mrr_dollars: number;
    shopify_ltv_dollars: number;
  };
}

const FanLadder: React.FC<{ data?: FanLadderData }> = ({ data }) => {
  if (!data) {
    return (
      <Panel label="// FAN LADDER //">
        <Skeleton variant="rectangular" height={320} />
      </Panel>
    );
  }
  const top = data.steps[0]?.count || 1;
  return (
    <Panel label="// FAN LADDER — TRIERS → LISTENERS → MEMBERS → PATRONS //">
      <Typography
        variant="caption"
        sx={{ color: '#5aaa5a', display: 'block', mb: 2, fontFamily: "'VT323', monospace" }}
      >
        &gt; the conversion lever between each step is named below the bar — that's where the work happens_
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {data.steps.map((step, i) => {
          const widthPct = (step.count / top) * 100;
          return (
            <React.Fragment key={step.key}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                <Box sx={{ width: 220, textAlign: 'right' }}>
                  <Typography
                    sx={{ color: step.color, fontFamily: "'VT323', monospace", fontSize: 16, fontWeight: 700, lineHeight: 1.1 }}
                  >
                    {step.label}
                  </Typography>
                  {step.sublabel && (
                    <Typography sx={{ color: '#5aaa5a', fontFamily: "'VT323', monospace", fontSize: 11 }}>
                      {step.sublabel}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ flex: 1, position: 'relative', height: 40 }}>
                  <Box sx={{
                    width: `${widthPct}%`,
                    minWidth: 60,
                    height: '100%',
                    background: step.color,
                    opacity: 0.85,
                    display: 'flex',
                    alignItems: 'center',
                    px: 1.5,
                    borderRadius: 0.5,
                    transition: 'width 0.4s ease',
                  }}>
                    <Typography sx={{ color: '#0a0a0a', fontFamily: "'VT323', monospace", fontWeight: 700, fontSize: 18 }}>
                      {fmt(step.count)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ width: 200, fontFamily: "'VT323', monospace", fontSize: 13 }}>
                  {i === 0 ? (
                    <Typography sx={{ color: '#5aaa5a' }}>top of funnel</Typography>
                  ) : (
                    <>
                      <Typography sx={{ color: '#bfffbf' }}>
                        {step.pct_of_top}% of top
                      </Typography>
                      <Typography sx={{ color: '#5aaa5a' }}>
                        {step.pct_of_prev}% from prev
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
              {/* Conversion-lever label between steps */}
              {step.lever_to_next && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height: 28, mb: 0.5 }}>
                  <Box sx={{ width: 220 }} />
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: '#5aaa5a', fontFamily: "'VT323', monospace", fontSize: 11 }}>
                      ↓
                    </Typography>
                    <Typography sx={{
                      color: '#00ffff',
                      fontFamily: "'VT323', monospace",
                      fontSize: 12,
                      letterSpacing: 1,
                      borderLeft: '2px solid #00ffff',
                      pl: 1,
                    }}>
                      LEVER: {step.lever_to_next}
                    </Typography>
                  </Box>
                  <Box sx={{ width: 200 }} />
                </Box>
              )}
            </React.Fragment>
          );
        })}
      </Box>

      {/* Side metrics — merch + revenue. NOT in the main funnel because
          merch is a quarterly commercial outcome, not a fan-development stage. */}
      <Box sx={{
        mt: 3, p: 2, border: '1px dashed #1e3a1e', borderRadius: 1,
        background: 'rgba(0,255,65,0.03)',
      }}>
        <Typography sx={{ color: '#00ffff', fontFamily: "'VT323', monospace", fontSize: 14, mb: 1 }}>
          ┌── COMMERCIAL OUTCOMES (downstream of the funnel) ──┐
        </Typography>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h5" sx={{ color: '#ffea00', fontFamily: "'VT323', monospace" }}>
              {fmtMoney(data.revenue.patreon_mrr_dollars)}
            </Typography>
            <Typography variant="caption" sx={{ color: '#5aaa5a' }}>
              Patreon MRR
            </Typography>
          </Box>
          <Box>
            <Typography variant="h5" sx={{ color: '#00ff41', fontFamily: "'VT323', monospace" }}>
              {fmt(data.merch.shopify_customers)}
            </Typography>
            <Typography variant="caption" sx={{ color: '#5aaa5a' }}>
              merch customers
            </Typography>
          </Box>
          <Box>
            <Typography variant="h5" sx={{ color: '#ff6600', fontFamily: "'VT323', monospace" }}>
              {fmtMoney(data.merch.shopify_ltv_dollars)}
            </Typography>
            <Typography variant="caption" sx={{ color: '#5aaa5a' }}>
              merch lifetime revenue
            </Typography>
          </Box>
          <Box>
            <Typography variant="h5" sx={{ color: '#00ffff', fontFamily: "'VT323', monospace" }}>
              {data.merch.patron_and_customer_pct}%
            </Typography>
            <Typography variant="caption" sx={{ color: '#5aaa5a' }}>
              of merch buyers are patrons ({data.merch.patron_and_customer} of {data.merch.shopify_customers})
            </Typography>
          </Box>
          <Box>
            <Typography variant="h5" sx={{ color: '#ffb000', fontFamily: "'VT323', monospace" }}>
              {data.merch.customer_and_patron_pct}%
            </Typography>
            <Typography variant="caption" sx={{ color: '#5aaa5a' }}>
              of patrons buy merch
            </Typography>
          </Box>
        </Box>
      </Box>
    </Panel>
  );
};

// ── Per-episode listener curve ────────────────────────────────────────────
// Lifetime downloads of every numbered episode in publication order.
// The shape tells you the show's listener-retention story:
//   • S01E01 spike     = new-listener entry rate (back-catalog discovery)
//   • smooth decline   = healthy show with normal churn
//   • cliffs           = season changes / format pivots that lost people
//   • mid-season peaks = standout episodes worth replicating
// Bars colored by season so the season boundaries are visible at a glance.

interface ListenerCurveEp {
  season: number | null;
  episode: number | null;
  label: string;
  title: string;
  publish_date: string | null;
  downloads: number;
}

interface ListenerCurveData {
  episodes: ListenerCurveEp[];
  count: number;
}

const SEASON_COLORS = [
  '#00ff41', '#00ffff', '#ff6600', '#ffb000', '#ff3344',
  '#9d4edd', '#ff80ff', '#5aaa5a', '#ffea00', '#bfffbf',
  '#ff4500', '#00b8ff', '#ff1493', '#a3ff00',
];

const ListenerCurve: React.FC<{ data?: ListenerCurveData }> = ({ data }) => {
  const eps = data?.episodes || [];
  return (
    <Panel label="// PODCAST LISTENER-RETENTION CURVE //">
      <Typography
        variant="caption"
        sx={{ color: '#5aaa5a', display: 'block', mb: 1, fontFamily: "'VT323', monospace" }}
      >
        &gt; lifetime downloads per episode in pub order — colored by season; the curve IS your show's listener-retention story_
      </Typography>
      {eps.length === 0 ? (
        <Skeleton variant="rectangular" height={300} />
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart data={eps} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="dlFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#00ff41" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#00ff41" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e3a1e" />
            <XAxis dataKey="label" stroke="#5aaa5a" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis stroke="#5aaa5a" tick={{ fontSize: 11 }} />
            <Tooltip
              contentStyle={{
                background: '#0f0f0f',
                border: '1px solid #1e3a1e',
                color: '#00ff41',
                fontFamily: "'VT323', monospace",
              }}
              formatter={(v: number) => [fmt(v), 'downloads']}
              labelFormatter={(label, items) => {
                const d = items?.[0]?.payload as ListenerCurveEp | undefined;
                return d ? `${d.label}${d.title ? ': ' + d.title.replace(/^S\d+E\d+\s*[-–]\s*/, '') : ''}` : label;
              }}
            />
            <Area
              type="monotone"
              dataKey="downloads"
              stroke="#00ff41"
              fill="url(#dlFill)"
              strokeWidth={2}
              dot={(props: any) => {
                const { cx, cy, payload, key } = props;
                if (cx == null || cy == null || !payload?.season) {
                  return <circle key={key} cx={cx ?? 0} cy={cy ?? 0} r={0} />;
                }
                const seasonIdx = Number(payload.season);
                const color = SEASON_COLORS[(seasonIdx - 1) % SEASON_COLORS.length] || '#5aaa5a';
                const isSeasonOpener = payload.episode === 1;
                return (
                  <circle
                    key={key}
                    cx={cx}
                    cy={cy}
                    r={isSeasonOpener ? 5 : 2.5}
                    fill={color}
                    stroke={isSeasonOpener ? '#0a0a0a' : color}
                    strokeWidth={isSeasonOpener ? 1.5 : 0}
                  />
                );
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
};

// ── Per-platform overview card ─────────────────────────────────────────────

interface PlatformCardProps {
  title: string;
  Icon: React.ElementType;
  configured: boolean;
  metrics: { label: string; value: string }[];
  onGo: () => void;
  accent?: string;
}

const PlatformCard: React.FC<PlatformCardProps> = ({
  title, Icon, configured, metrics, onGo, accent = '#00ff41',
}) => (
  <Panel label={`[ ${title} ]`}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
      <Icon sx={{ color: accent, fontSize: 36, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {!configured ? (
          <Typography variant="body2" color="text.secondary">
            NOT CONFIGURED
          </Typography>
        ) : (
          <>
            {metrics.map((m) => (
              <Box
                key={m.label}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
                >
                  {m.label}
                </Typography>
                <Typography variant="body1" fontWeight={600} sx={{ color: accent }}>
                  {m.value}
                </Typography>
              </Box>
            ))}
          </>
        )}
        <Button
          size="small"
          onClick={onGo}
          endIcon={<LaunchRounded fontSize="small" />}
          sx={{ mt: 1.5, color: accent, fontSize: 10, letterSpacing: '0.08em' }}
        >
          [ OPEN ]
        </Button>
      </Box>
    </Box>
  </Panel>
);

const HeroKPI: React.FC<{
  label: string; value: string; sublabel?: string;
  Icon: React.ElementType; accent?: string;
}> = ({ label, value, sublabel, Icon, accent = '#00ff41' }) => (
  <Panel label={label}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Icon sx={{ color: accent, fontSize: 48 }} />
      <Box>
        <Typography
          variant="h3"
          fontWeight={700}
          sx={{ color: accent, fontFamily: "'Press Start 2P', monospace", fontSize: '26px' }}
        >
          {value}
        </Typography>
        {sublabel && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}
          >
            {sublabel}
          </Typography>
        )}
      </Box>
    </Box>
  </Panel>
);

// ── Main dashboard ─────────────────────────────────────────────────────────

interface MasterDashboardProps {
  onNavigate?: (view: string) => void;
}

const MasterDashboard: React.FC<MasterDashboardProps> = ({ onNavigate }) => {
  const go = (view: string) => onNavigate?.(view);

  const { data: youtubeSum } = useQuery({
    queryKey: ['mdash-yt-summary'], queryFn: youtubeAPI.getSummary, staleTime: 60000,
  });
  const { data: youtubeKpi } = useQuery({
    queryKey: ['mdash-yt-kpis'], queryFn: youtubeAPI.getKPIs, staleTime: 60000,
  });
  const { data: youtubeTimeline } = useQuery({
    // 365d so the chart shows a full year of trend rather than a tiny
    // 30d window. personal_channel_analytics has 1175+ rows of daily data.
    queryKey: ['mdash-yt-timeline', 365], queryFn: () => youtubeAPI.getTimeline(365), staleTime: 60000,
  });
  const { data: megaphone } = useQuery({
    queryKey: ['mdash-mg'], queryFn: megaphoneAPI.getOverview, staleTime: 60000,
  });
  const { data: patreon } = useQuery({
    queryKey: ['mdash-pt'], queryFn: patreonAPI.getOverview, staleTime: 60000,
  });
  const { data: patreonHist } = useQuery({
    queryKey: ['mdash-pt-hist'], queryFn: () => patreonAPI.getHistory(30), staleTime: 60000,
  });
  const { data: shopify } = useQuery({
    queryKey: ['mdash-sh'], queryFn: shopifyAPI.getOverview, staleTime: 60000,
  });
  const { data: reddit } = useQuery({
    queryKey: ['mdash-rd'], queryFn: redditAPI.getOverview, staleTime: 60000,
  });
  const { data: tiktok } = useQuery({
    queryKey: ['mdash-tt'], queryFn: tiktokAPI.getOverview, staleTime: 60000,
  });
  const { data: tiktokHist } = useQuery({
    // Match YouTube's 365d window. The /history endpoint falls back to
    // per-video attribution from tiktok.videos for dates older than the
    // 12-day TikTok Studio window — so we get 2 years of fill instead of
    // a flat line.
    queryKey: ['mdash-tt-hist', 365], queryFn: () => tiktokAPI.getHistory(365), staleTime: 60000,
  });
  const { data: ig } = useQuery({
    queryKey: ['mdash-ig'], queryFn: metaAPI.getInstagramOverview, staleTime: 60000,
  });
  const { data: fb } = useQuery({
    queryKey: ['mdash-fb'], queryFn: metaAPI.getFacebookOverview, staleTime: 60000,
  });
  const { data: masterCal } = useQuery({
    queryKey: ['mdash-cal'], queryFn: () => masterCalendarAPI.get(6), staleTime: 60000,
  });
  const { data: unfulfilled } = useQuery({
    queryKey: ['mdash-unfulfilled'],
    queryFn: () => shopifyAPI.getUnfulfilled(50),
    staleTime: 30000,   // 30s — this drives the ACTION REQUIRED banner
    refetchInterval: 60000, // poll every minute so new orders surface without a manual refresh
  });
  const { data: episodeCorrelation } = useQuery({
    queryKey: ['mdash-episode-correlation'],
    queryFn: () => episodeCorrelationAPI.get(20),
    staleTime: 5 * 60_000,
  });
  const { data: ytRetention } = useQuery({
    queryKey: ['mdash-yt-retention', 24],
    queryFn: () => youtubeRetentionAPI.getHeatmap(24),
    staleTime: 5 * 60_000,
  });
  const { data: ytTrafficMix } = useQuery({
    queryKey: ['mdash-yt-traffic', 26],
    queryFn: () => youtubeTrafficMixAPI.get(26),
    staleTime: 5 * 60_000,
  });
  const { data: fanLadder } = useQuery({
    queryKey: ['mdash-fan-ladder'],
    queryFn: () => fanLadderAPI.get(),
    staleTime: 10 * 60_000,
  });
  const { data: listenerCurve } = useQuery({
    queryKey: ['mdash-listener-curve'],
    queryFn: () => podcastListenerCurveAPI.get(),
    staleTime: 10 * 60_000,
  });

  // ── Hero KPIs ──────────────────────────────────────────────────────────
  const audience =
    (youtubeKpi?.subscribers || 0) +
    (patreon?.campaign?.patron_count || 0) +
    (reddit?.subscribers || 0) +
    (tiktok?.follower_count || 0) +
    (ig?.followers_count || 0) +
    (fb?.fan_count || 0);

  const merchRevenue = shopify?.total_revenue || 0;
  const patreonMRR = patreon?.campaign?.pledge_sum_cents
    ? patreon.campaign.pledge_sum_cents / 100
    : null;
  const episodeCount = megaphone?.episode_count || 0;

  // ── Calendar events (colored by platform type) ─────────────────────────
  const calendarEvents: CalendarEvent[] = (masterCal?.events || []).map((e: any) => ({
    date: e.date,
    title: e.title,
    color: PLATFORM_COLORS[e.type] || '#00ff41',
    type: e.type,
    url: e.url || undefined,
  }));

  // ── Unified timeline (30d) ─────────────────────────────────────────────
  const timelineMap = new Map<string, any>();
  (youtubeTimeline?.timeline || []).forEach((r: any) => {
    timelineMap.set(r.date, { date: r.date, youtube_views: r.youtube });
  });
  (tiktokHist?.daily || []).forEach((r: any) => {
    const prev = timelineMap.get(r.date) || { date: r.date };
    prev.tiktok_views = r.video_views;
    timelineMap.set(r.date, prev);
  });
  const timeline = Array.from(timelineMap.values()).sort((a, b) =>
    a.date.localeCompare(b.date),
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      <Box mb={4}>
        <SectionTitle sub="&gt; cross-platform audience, revenue, content velocity_">
          AUTOPOD :: MASTER CONSOLE
        </SectionTitle>
        {youtubeSum?.channel?.title && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            &gt; {youtubeSum.channel.title}
            {episodeCount ? ` // ${episodeCount} episodes` : ''}
            {megaphone?.first_episode && megaphone?.latest_episode &&
              ` // ${megaphone.first_episode} → ${megaphone.latest_episode}`}
          </Typography>
        )}
      </Box>

      {/* Unfulfilled-orders banner — sits ABOVE the calendar so it's the first
          thing you see when orders need shipping. Auto-polls every 60s. */}
      <UnfulfilledBanner data={unfulfilled} onGo={() => go('shopify')} />

      {/* 1. Release calendar — front and center */}
      <Box mb={4}>
        {calendarEvents.length ? (
          <ReleaseCalendar
            events={calendarEvents}
            legend={CALENDAR_LEGEND}
            heading="// MASTER RELEASE CALENDAR //"
          />
        ) : (
          <Panel label="// MASTER RELEASE CALENDAR //">
            <Skeleton variant="rectangular" height={480} />
          </Panel>
        )}
      </Box>

      {/* 2. Hero KPIs */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <HeroKPI
            label="// TOTAL AUDIENCE //"
            value={fmt(audience)}
            sublabel="across all platforms"
            Icon={People}
            accent="#00ff41"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <HeroKPI
            label="// PATRONS //"
            value={fmt(patreon?.campaign?.patron_count)}
            sublabel={patreonMRR != null ? `${fmtMoney(patreonMRR)}/mo pledged` : 'active patrons'}
            Icon={Favorite}
            accent="#ff6600"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <HeroKPI
            label="// EPISODES //"
            value={fmt(episodeCount)}
            sublabel={megaphone?.season_count ? `${megaphone.season_count} seasons` : 'podcast library'}
            Icon={Podcasts}
            accent="#00ffff"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <HeroKPI
            label="// MERCH REVENUE //"
            value={fmtMoney(merchRevenue)}
            sublabel={shopify?.order_count ? `${shopify.order_count} orders lifetime` : ''}
            Icon={AttachMoney}
            accent="#ffb000"
          />
        </Grid>
      </Grid>

      {/* 3. Unified audience timeline */}
      <Box sx={{ mb: 4 }}>
        <Panel label="// AUDIENCE ACTIVITY (365D) //">
          {timeline.length ? (
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={timeline} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="ytFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ff3344" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#ff3344" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="ttFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00ffff" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#00ffff" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a1e" />
                <XAxis dataKey="date" stroke="#5aaa5a" tick={{ fontSize: 12 }} />
                <YAxis stroke="#5aaa5a" tick={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    background: '#0f0f0f',
                    border: '1px solid #1e3a1e',
                    color: '#00ff41',
                    fontFamily: "'VT323', monospace",
                  }}
                  formatter={(v: number) => fmt(v)}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="youtube_views" name="YouTube views" stroke="#ff3344" fill="url(#ytFill)" strokeWidth={2} />
                <Area type="monotone" dataKey="tiktok_views" name="TikTok views" stroke="#00ffff" fill="url(#ttFill)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <Skeleton variant="rectangular" height={320} />}
        </Panel>
      </Box>

      {/* 3.3 Per-episode listener-retention curve */}
      <Box sx={{ mb: 4 }}>
        <ListenerCurve data={listenerCurve} />
      </Box>

      {/* 3.4 Fan ladder — podcast funnel */}
      <Box sx={{ mb: 4 }}>
        <FanLadder data={fanLadder} />
      </Box>

      {/* 3.5 Cross-platform episode correlation */}
      <Box sx={{ mb: 4 }}>
        <EpisodeCorrelationPanel data={episodeCorrelation} />
      </Box>

      {/* 3.6 YouTube discovery mix — weekly traffic-source stacked area */}
      <Box sx={{ mb: 4 }}>
        <TrafficMixChart data={ytTrafficMix} />
      </Box>

      {/* 3.7 YouTube retention heatmap — per-video drop-off curves */}
      <Box sx={{ mb: 4 }}>
        <RetentionHeatmap data={ytRetention} />
      </Box>

      {/* 4. Patron growth (separate because different scale) */}
      {patreonHist?.available && (
        <Box sx={{ mb: 4 }}>
          <Panel label="// PATRON GROWTH (30D) //">
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={patreonHist.daily} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a1e" />
                <XAxis dataKey="date" stroke="#5aaa5a" tick={{ fontSize: 12 }} />
                <YAxis stroke="#5aaa5a" domain={['dataMin - 5', 'dataMax + 5']} tick={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    background: '#0f0f0f',
                    border: '1px solid #1e3a1e',
                    color: '#00ff41',
                    fontFamily: "'VT323', monospace",
                  }}
                  formatter={(v: number) => fmt(v)}
                />
                <Area type="monotone" dataKey="patron_count" name="Patrons" stroke="#ff6600" fill="#ff6600" fillOpacity={0.2} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </Panel>
        </Box>
      )}

      {/* 5. Platform grid — quick jumps */}
      <Box mb={2}>
        <Typography variant="h6" sx={{ color: '#5aaa5a', letterSpacing: '0.08em', mb: 2 }}>
          &gt; PLATFORMS
        </Typography>
      </Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <PlatformCard
            title="YOUTUBE"
            Icon={YouTube}
            configured={!!youtubeSum}
            accent="#ff3344"
            onGo={() => go('youtube')}
            metrics={[
              { label: 'Subscribers', value: fmt(youtubeKpi?.subscribers) },
              { label: 'Reach (30d)',  value: fmt(youtubeKpi?.totalReach) },
              { label: 'Engagement',   value: fmtPct(youtubeKpi?.engagement) },
              { label: 'Videos',       value: fmt(youtubeKpi?.videoCount) },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <PlatformCard
            title="PODCAST"
            Icon={Podcasts}
            configured={!!megaphone && (megaphone.episode_count || 0) > 0}
            accent="#00ffff"
            onGo={() => go('megaphone')}
            metrics={[
              { label: 'Episodes', value: fmt(megaphone?.episode_count) },
              { label: 'Seasons',  value: fmt(megaphone?.season_count) },
              { label: 'Shows',    value: fmt(megaphone?.show_count) },
              {
                label: 'Avg episode',
                value: megaphone?.avg_duration_seconds
                  ? `${Math.round(megaphone.avg_duration_seconds / 60)}m`
                  : '—',
              },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <PlatformCard
            title="PATREON"
            Icon={Favorite}
            configured={!!patreon?.configured}
            accent="#ff6600"
            onGo={() => go('patreon')}
            metrics={[
              { label: 'Patrons',       value: fmt(patreon?.campaign?.patron_count) },
              { label: 'Paid members',  value: fmt(patreon?.campaign?.paid_member_count) },
              { label: 'MRR pledged',   value: patreonMRR != null ? fmtMoney(patreonMRR) : '—' },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <PlatformCard
            title="SHOPIFY"
            Icon={ShoppingCart}
            configured={!!shopify?.configured}
            accent="#ffb000"
            onGo={() => go('shopify')}
            metrics={[
              { label: 'Orders',    value: fmt(shopify?.order_count) },
              { label: 'Revenue',   value: fmtMoney(shopify?.total_revenue) },
              { label: 'Avg order', value: fmtMoney(shopify?.avg_order_value) },
              { label: 'Products',  value: fmt(shopify?.product_count) },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <PlatformCard
            title="TIKTOK"
            Icon={MusicVideo}
            configured={!!tiktok?.configured}
            accent="#00ffff"
            onGo={() => go('tiktok')}
            metrics={[
              { label: 'Followers',   value: fmt(tiktok?.follower_count) },
              { label: 'Videos',      value: fmt(tiktok?.video_count) },
              { label: 'Total views', value: fmt(tiktok?.total_views) },
              { label: 'Total likes', value: fmt(tiktok?.likes_count) },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <PlatformCard
            title="REDDIT"
            Icon={Forum}
            configured={!!reddit?.configured}
            accent="#ff6600"
            onGo={() => go('reddit')}
            metrics={[
              { label: 'Subscribers',   value: fmt(reddit?.subscribers) },
              { label: 'Subreddits',    value: fmt(reddit?.subreddit_count) },
              { label: 'Posts indexed', value: fmt(reddit?.total_posts) },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <PlatformCard
            title="INSTAGRAM"
            Icon={CameraAlt}
            configured={!!ig?.configured}
            accent="#e1306c"
            onGo={() => go('meta')}
            metrics={[
              { label: 'Followers', value: fmt(ig?.followers_count) },
              { label: 'Media',     value: fmt(ig?.media_count) },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <PlatformCard
            title="FACEBOOK"
            Icon={CameraAlt}
            configured={!!fb?.configured}
            accent="#1877f2"
            onGo={() => go('meta')}
            metrics={[
              { label: 'Fans',      value: fmt(fb?.fan_count) },
              { label: 'Followers', value: fmt(fb?.followers_count) },
            ]}
          />
        </Grid>
      </Grid>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', textAlign: 'center', mt: 4, letterSpacing: '0.08em' }}
      >
        &gt; autopod // analytics // end of console_
      </Typography>
    </Container>
  );
};

export default MasterDashboard;
